import type { Solution } from "../types";
import type { CandidateSet, ProblemProfile, SpecialtyMatch } from "./types";

/**
 * UC-2b. Intersect the profile with the roster: a Board Member is a candidate iff active AND at
 * least one specialty (primary or secondary — unioned, no precision distinction here) is in the
 * profile's tags. Deliberately broad — this is a safety filter, not a precision filter
 * (decomposition doc 5.3). An inactive Board Member can never appear, under any input; that's the
 * ADR-006 guarantee and it is unconditional, checked before the tag intersection so no profile
 * shape can route around it.
 *
 * Zero candidates is a normal, valid outcome (short-circuits to the fallback path with no model
 * call, per ADR-008) — not an error.
 *
 * While iterating, also records which profile tags matched each candidate and whether via their
 * primary or secondary specialty (matchedTagsBySolutionId) — not used by the filter decision
 * itself, but passed through for UC-2c's prompt so the model knows why each candidate is present,
 * not just that they are.
 */
export function selectCandidateSet(profile: ProblemProfile, solutions: Solution[]): CandidateSet {
  const profileTagIds = new Set(profile.weightedTags.map((wt) => wt.tagId));

  const solutionIds: string[] = [];
  const matchedTagsBySolutionId: Record<string, SpecialtyMatch[]> = {};

  for (const s of solutions) {
    if (!s.active) continue;

    const matches: SpecialtyMatch[] = [];
    for (const tagId of s.primarySpecialties) {
      if (profileTagIds.has(tagId)) matches.push({ tagId, via: "primary" });
    }
    const primaryMatchedTagIds = new Set(matches.map((m) => m.tagId));
    for (const tagId of s.secondarySpecialties) {
      if (profileTagIds.has(tagId) && !primaryMatchedTagIds.has(tagId)) matches.push({ tagId, via: "secondary" });
    }

    if (matches.length > 0) {
      solutionIds.push(s.id);
      matchedTagsBySolutionId[s.id] = matches;
    }
  }

  return { solutionIds, isEmpty: solutionIds.length === 0, matchedTagsBySolutionId };
}
